"use client";

import { motion } from "framer-motion";
import { MessageCircle, Send, Hash, MessagesSquare, ShieldCheck, Smartphone, Cpu, FileText, Globe, Terminal, Code2 } from "lucide-react";

const channels = [
  { name: "WhatsApp", caption: "Control your machine from your phone.", icon: MessageCircle },
  { name: "Telegram", caption: "Fast, dev-friendly channel for everything.", icon: Send },
  { name: "Discord", caption: "Your AI in your dev or community server.", icon: Hash },
  { name: "Slack", caption: "Drop it into your workspace as a teammate.", icon: MessagesSquare },
  { name: "Signal", caption: "Encrypted conversations with your assistant.", icon: ShieldCheck },
  { name: "iMessage", caption: "Talk to your AI like any other contact.", icon: Smartphone },
];

const models = [
  { name: "Claude", provider: "Anthropic" },
  { name: "GPT", provider: "OpenAI" },
  { name: "Gemini", provider: "Google" },
  { name: "OpenRouter", provider: "Multi-provider" },
  { name: "Ollama / Local", provider: "Self-hosted" },
];

const skills = [
  { text: "Read/write files on your machine", icon: FileText },
  { text: "Run shell commands, execute scripts", icon: Terminal },
  { text: "Browse, fill forms, scrape pages", icon: Globe },
  { text: "Talk to APIs and internal tools via skills", icon: Code2 },
  { text: "Build or extend custom skills for your workflows", icon: Cpu },
];

export const IntegrationsChannelsSection = () => {
  return (
    <section id="integrations" className="section-spacing overflow-hidden relative">

      <div className="section-container relative z-10">
        <div className="max-w-6xl mx-auto space-y-16">
          <motion.div
            className="text-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-3xl md:text-5xl font-display font-medium leading-tight">
              Integrations & Channels
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Clawbiz can sit behind 10+ messaging channels; we connect the ones you actually live in.
            </p>
          </motion.div>

          {/* Channels grid */}
          <div>
            <h3 className="text-xl font-display font-medium mb-6 text-center text-foreground/80">Channels</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {channels.map((ch, i) => {
                const Icon = ch.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="relative group"
                  >
                    <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-[hsl(var(--cyan-bright)/0.1)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative glass-card rounded-2xl p-4 space-y-2 hover:bg-white/[0.04] transition-all duration-300">
                      <Icon className="w-5 h-5 text-[hsl(var(--cyan-bright))]" />
                      <p className="font-medium text-sm">{ch.name}</p>
                      <p className="text-xs text-muted-foreground">{ch.caption}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Models */}
          <div>
            <h3 className="text-xl font-display font-medium mb-6 text-center text-foreground/80">Models & Intelligence</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {models.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="glass-card rounded-xl px-5 py-3 text-center hover:bg-white/[0.04] transition-all duration-300"
                >
                  <p className="font-medium text-sm">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.provider}</p>
                </motion.div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground text-center mt-4 max-w-xl mx-auto font-mono">
              You pick the brain. We wire Clawbiz to it and tune routing for your workloads.
            </p>
          </div>

          {/* Skills */}
          <div>
            <h3 className="text-xl font-display font-medium mb-6 text-center text-foreground/80">Skills & Tools</h3>
            <ul className="max-w-md mx-auto space-y-3">
              {skills.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.li
                    key={i}
                    className="flex items-center gap-3 text-sm text-foreground/80"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                  >
                    <Icon className="w-4 h-4 text-[hsl(var(--cyan-bright))] shrink-0" />
                    {s.text}
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
